import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Home, TrendingUp } from "lucide-react";

const formatCurrency = (value) => {
  return "$" + formatNumber(value);
};

const formatNumber = (value, decimals = 0) => {
  if (isNaN(value) || !isFinite(value)) {
    return decimals > 0 ? (0).toFixed(decimals) : "0";
  }
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatPercentOrDash = (value, decimals = 1) => {
  if (!Number.isFinite(value)) return "—";
  return `${formatNumber(value, decimals)}%`;
};

const npv = (rate, cashflows) => {
  if (!Number.isFinite(rate) || rate <= -1) return NaN;
  return cashflows.reduce((sum, c) => {
    const t = c.days / 365;
    return sum + c.amount / Math.pow(1 + rate, t);
  }, 0);
};

const dnpv = (rate, cashflows) => {
  if (!Number.isFinite(rate) || rate <= -1) return NaN;
  return cashflows.reduce((sum, c) => {
    const t = c.days / 365;
    return sum - (t * c.amount) / Math.pow(1 + rate, t + 1);
  }, 0);
};

const computeXirrPercent = (cashflows) => {
  if (!cashflows || cashflows.length < 2) return NaN;
  const hasPos = cashflows.some((c) => c.amount > 0);
  const hasNeg = cashflows.some((c) => c.amount < 0);
  if (!hasPos || !hasNeg) return NaN;

  let r = 0.12;
  for (let i = 0; i < 50; i += 1) {
    const v = npv(r, cashflows);
    const dv = dnpv(r, cashflows);
    if (!Number.isFinite(v) || !Number.isFinite(dv) || Math.abs(dv) < 1e-14)
      break;
    const next = r - v / dv;
    if (!Number.isFinite(next) || next <= -0.9999) break;
    if (Math.abs(next - r) < 1e-7) {
      r = next;
      break;
    }
    r = Math.min(Math.max(next, -0.99), 100);
  }

  const finalNpv = npv(r, cashflows);
  if (Number.isFinite(finalNpv) && Math.abs(finalNpv) < 1e-4) {
    return r * 100;
  }

  let lo = -0.5;
  let hi = 2;
  let nLo = npv(lo, cashflows);
  let nHi = npv(hi, cashflows);
  let expand = 0;
  while (
    (!Number.isFinite(nLo) || !Number.isFinite(nHi) || nLo * nHi > 0) &&
    expand < 40
  ) {
    hi *= 1.25;
    nHi = npv(hi, cashflows);
    expand += 1;
  }
  if (!Number.isFinite(nLo) || !Number.isFinite(nHi) || nLo * nHi > 0) {
    return NaN;
  }

  for (let i = 0; i < 100; i += 1) {
    const mid = (lo + hi) / 2;
    const nMid = npv(mid, cashflows);
    if (!Number.isFinite(nMid)) return NaN;
    if (Math.abs(nMid) < 1e-7) return mid * 100;
    if (nLo * nMid <= 0) {
      hi = mid;
      nHi = nMid;
    } else {
      lo = mid;
      nLo = nMid;
    }
  }
  return ((lo + hi) / 2) * 100;
};

/**
 * Read-only distribution bar showing proportional segments
 */
const DistributionBar = ({ segments }) => {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  if (total === 0) return null;

  return (
    <div className="w-full px-2 sm:px-0 mb-2">
      <div className="h-3 sm:h-3.5 w-full rounded-full overflow-hidden flex shadow-inner">
        {segments.map((segment, i) => {
          const pct = (Math.max(0, segment.value) / total) * 100;
          if (pct <= 0) return null;
          return (
            <div
              key={i}
              className="h-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                backgroundColor: segment.color,
                minWidth: pct > 0 ? "4px" : "0",
              }}
              title={`${segment.label}: ${formatCurrency(segment.value)} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] sm:text-xs text-gray-500 px-1">
        <span>0%</span>
        <span>100%</span>
      </div>
    </div>
  );
};

/**
 * Circular display for a financial value (matches ProfitDistributionCard style)
 */
const ValueCircle = ({ label, value, percentage, bgColor, textColor }) => {
  return (
    <div className="text-center flex flex-col items-center">
      <div
        className={`inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full mb-2 ${bgColor}`}
      >
        <div>
          <p
            className={`text-xs sm:text-sm md:text-base font-bold ${textColor} leading-tight`}
          >
            {formatCurrency(value)}
          </p>
        </div>
      </div>
      <p className="font-medium text-[11px] sm:text-sm text-gray-800 leading-tight max-w-[90px] sm:max-w-none">
        {label}
      </p>
    </div>
  );
};

export function BarCharts({ results = {} }) {
  // Handle both old flat structure and new nested structure from backend
  const initialValue =
    results.property?.presentValue || results.presentValue || 0;
  const rentalIncome =
    results.rental?.upfrontRentAmount || results.upfrontRental || 0;
  const profit = results.profit?.ownerReturnAmount || results.ownerProfit || 0;
  const guaranteedReturn =
    results.investorExample?.guaranteedReturn || results.guaranteedReturn || 0;
  const guaranteedReturnPct =
    results.profit?.investorReturnRate || results.guaranteedReturnPct || 0;
  const loanAmount =
    results.costs?.totalInvestmentNeeded || results.totalLoan || 0;
  const interest =
    results.financing?.totalInterestPaid || results.loanInterest || 0;
  const currentOpenLoan = results.financing?.loanAmount || results.loan || 0;
  const profitShare =
    results.investorExample?.profitShare || results.investorProfit || 0;
  const rentalPeriod =
    results.rental?.rentalPeriodMonths || results.rentalPeriod || 12;

  // Owner distribution segments
  const ownerSegments = useMemo(
    () => [
      {
        label: "Initial Property Value",
        value: initialValue,
        color: "#9ca3af",
      },
      { label: "Upfront Rental Income", value: rentalIncome, color: "#c96442" },
      { label: "Guaranteed Return", value: guaranteedReturn, color: "#f59e0b" },
      { label: "Extra Proceed", value: profit, color: "#10b981" },
    ],
    [initialValue, rentalIncome, guaranteedReturn, profit],
  );

  const ownerTotal = initialValue + rentalIncome + guaranteedReturn + profit;
  const ownerTotalProfitByOwner = rentalIncome + guaranteedReturn + profit;
  const ownerCashInHand = ownerTotal - currentOpenLoan;
  const ownerROI =
    initialValue > 0
      ? ((ownerTotalProfitByOwner / initialValue) * 100).toFixed(2)
      : 0;

  // Investor distribution segments
  const investorSegments = useMemo(
    () => [
      { label: "Loan Repayment", value: loanAmount, color: "#93c5fd" },
      { label: "Interest Earned", value: interest, color: "#8b5cf6" },
      { label: "Extra Proceed", value: profitShare, color: "#c96442" },
    ],
    [loanAmount, interest, profitShare],
  );

  const investorTotal = loanAmount + interest + profitShare;
  const investorProfitAmount = interest + profitShare;
  const investorRoiPercent =
    loanAmount > 0 ? (investorProfitAmount / loanAmount) * 100 : NaN;
  const investorAnnualReturnPercent =
    Number(rentalPeriod) > 0 && Number.isFinite(investorRoiPercent)
      ? investorRoiPercent * (12 / Number(rentalPeriod))
      : NaN;
  const upfrontAmount = Number(results.upfrontAmount) || 0;
  const parts = Math.max(1, Number(results.paymentParts) || 1);
  const stagedPerPart = Number(results.amountPerPart) || 0;
  const months = Number(rentalPeriod) || 0;
  const investorXirrPercent = computeXirrPercent(
    months > 0
      ? [
          ...(upfrontAmount > 0 ? [{ days: 0, amount: -upfrontAmount }] : []),
          ...Array.from({ length: parts }, (_, idx) => {
            const i = idx + 1;
            return {
              days: Math.round((30 * months * i) / (parts + 1)),
              amount: -stagedPerPart,
            };
          }),
          { days: Math.round(30 * months), amount: investorTotal },
        ]
      : []
  );
  const investorROI =
    loanAmount > 0 && rentalPeriod > 0
      ? (
          ((interest + profitShare) / (loanAmount * (rentalPeriod / 12))) *
          100
        ).toFixed(2)
      : 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Owner's Financial Outcome */}
      <Card className="shadow-md">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
            <Home className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
            Owner's Financial Outcome
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-4">
            Breakdown of total owner payoff including initial value, rental
            income, guaranteed return, and extra proceeds.
          </p>

          {/* Distribution Bar */}
          <DistributionBar segments={ownerSegments} />

          {/* Legend under bar */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mb-5 mt-1 px-1">
            {ownerSegments.map((seg, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: seg.color }}
                />
                <span className="text-[10px] sm:text-xs text-gray-600">
                  {seg.label}
                </span>
              </div>
            ))}
          </div>

          {/* Value Circles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <ValueCircle
              label="Initial Value"
              value={initialValue}
              percentage={
                ownerTotal > 0
                  ? `${((initialValue / ownerTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor="bg-gray-100"
              textColor="text-gray-700"
            />
            <ValueCircle
              label="Upfront Rental"
              value={rentalIncome}
              percentage={
                ownerTotal > 0
                  ? `${((rentalIncome / ownerTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor="bg-amber-50"
              textColor="text-amber-700"
            />
            <ValueCircle
              label={`Guaranteed (${guaranteedReturnPct}%)`}
              value={guaranteedReturn}
              percentage={
                ownerTotal > 0
                  ? `${((guaranteedReturn / ownerTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor="bg-yellow-50"
              textColor="text-yellow-700"
            />
            <ValueCircle
              label="Extra Proceed"
              value={profit}
              percentage={
                ownerTotal > 0
                  ? `${((Math.max(0, profit) / ownerTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor={profit >= 0 ? "bg-green-50" : "bg-red-50"}
              textColor={profit >= 0 ? "text-green-700" : "text-red-600"}
            />
            <ValueCircle
              label="Total Profit by Owner (Upfront Rental + Guaranteed Return + Extra Proceeds)"
              value={ownerTotalProfitByOwner}
              percentage={
                ownerTotal > 0
                  ? `${((Math.max(0, ownerTotalProfitByOwner) / ownerTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor={
                ownerTotalProfitByOwner >= 0 ? "bg-emerald-50" : "bg-red-50"
              }
              textColor={
                ownerTotalProfitByOwner >= 0
                  ? "text-emerald-700"
                  : "text-red-600"
              }
            />
          </div>

          {/* Summary Stats */}
          <div className="space-y-3 mt-5 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">
                  Total Cash In Hand{" "}
                  <span className="text-gray-400">(Payoff - Open Loan)</span>
                </p>
                <p className="text-sm sm:text-base font-bold text-gray-900">
                  {formatCurrency(ownerTotal)} -{" "}
                  {formatCurrency(currentOpenLoan)} ={" "}
                  {formatCurrency(ownerCashInHand)}
                </p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">
                  Return on Initial Value
                </p>
                <p className="text-sm sm:text-base font-bold text-amber-700">
                  {ownerROI}%
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investor's Financial Outcome */}
      <Card className="shadow-md">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg md:text-xl">
            <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
            Investor's Financial Outcome
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <p className="text-xs sm:text-sm font-semibold text-gray-800 mb-4">
            Breakdown of total investor return including loan repayment,
            interest earned, and extra proceeds.
          </p>

          {/* Distribution Bar */}
          <DistributionBar segments={investorSegments} />

          {/* Legend under bar */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mb-5 mt-1 px-1">
            {investorSegments.map((seg, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: seg.color }}
                />
                <span className="text-[10px] sm:text-xs text-gray-600">
                  {seg.label}
                </span>
              </div>
            ))}
          </div>

          {/* Value Circles */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <ValueCircle
              label="Loan Repayment"
              value={loanAmount}
              percentage={
                investorTotal > 0
                  ? `${((loanAmount / investorTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor="bg-blue-50"
              textColor="text-blue-700"
            />
            <ValueCircle
              label="Interest Earned"
              value={interest}
              percentage={
                investorTotal > 0
                  ? `${((interest / investorTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor="bg-purple-50"
              textColor="text-purple-700"
            />
            <ValueCircle
              label="Extra Proceed"
              value={profitShare}
              percentage={
                investorTotal > 0
                  ? `${((profitShare / investorTotal) * 100).toFixed(1)}%`
                  : "0%"
              }
              bgColor={profitShare >= 0 ? "bg-amber-50" : "bg-red-50"}
              textColor={profitShare >= 0 ? "text-amber-700" : "text-red-600"}
            />
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5 pt-4 border-t border-gray-200">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">
                Total Return
              </p>
              <p className="text-sm sm:text-base font-bold text-gray-900">
                {formatCurrency(investorTotal)}
              </p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">
                Total Return on Investment
              </p>
              <p className="text-sm sm:text-base font-bold text-amber-700">
                {formatCurrency(investorTotal - loanAmount)}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">
                Investor ROI
              </p>
              <p className="text-sm sm:text-base font-bold text-blue-700">
                {formatPercentOrDash(investorRoiPercent, 1)}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">
                Annual Return
              </p>
              <p className="text-sm sm:text-base font-bold text-purple-700">
                {formatPercentOrDash(investorAnnualReturnPercent, 1)}
              </p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3">
              <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5">
                Investor XIRR
              </p>
              <p className="text-sm sm:text-base font-bold text-indigo-700">
                {formatPercentOrDash(investorXirrPercent, 1)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default BarCharts;
